import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  signInWithGoogle,
  signInWithApple,
  handleAppleRedirectResult,
  handleAuthError,
} from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { getFinancialColor } from '../../theme';

/**
 * 로그인 페이지 — Wanted DS Green Split Layout
 * 좌측: 브랜드 스토리텔링 패널 (그라데이션 + 그래프 + 가치 제안)
 * 우측: 인증 카드 (Google / Apple 소셜 로그인)
 *
 * 모바일(900px 이하)에서는 브랜드 패널을 숨기고 인증 카드만 풀폭으로.
 * 핸들러(signInWithGoogle/Apple, handleAppleRedirectResult)는 그대로 유지.
 */
const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState<'google' | 'apple' | null>(null);
  const [pressed, setPressed] = useState<'google' | 'apple' | null>(null);
  const [mounted, setMounted] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  // 페이지 진입 애니메이션
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  // 로그인되어 있지만 프리미엄이 아닌 경우에만 프리미엄 안내 추가 표시
  const isAuthenticatedNotPremium = !!user;

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const showMessage = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning' = 'info',
  ) => setSnackbar({ open: true, message, severity });

  // Apple 리다이렉트 결과 처리
  useEffect(() => {
    const run = async () => {
      try {
        const result = await handleAppleRedirectResult();
        if (result?.user) {
        }
      } catch (error) {
        console.error('ℹ️ Apple 리다이렉트 결과 없음 (정상):', error);
      }
    };
    run();
  }, []);

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      const result = await signInWithGoogle();
      if (result?.user) {
        showMessage('구글 로그인 성공!', 'success');
        setTimeout(() => navigate('/', { replace: true }), 500);
      }
    } catch (error) {
      const authError = handleAuthError(error as any);
      showMessage(authError.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    try {
      setLoading(true);
      const result = await signInWithApple();
      if (result && result.user) {
        showMessage('애플 로그인 성공!', 'success');
        setTimeout(() => navigate('/', { replace: true }), 500);
        setLoading(false);
      }
    } catch (error) {
      const authError = handleAuthError(error as any);
      showMessage(authError.message, 'error');
      setLoading(false);
    }
  };

  const eyeTransition =
    'opacity 500ms cubic-bezier(0.2,0.8,0.2,1) 100ms, transform 500ms cubic-bezier(0.2,0.8,0.2,1) 100ms';
  const cardTransition =
    'opacity 420ms cubic-bezier(0.2,0.8,0.2,1) 60ms, transform 420ms cubic-bezier(0.2,0.8,0.2,1) 60ms';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'grid',
        // 데스크톱: split, 모바일: 인증 카드만
        gridTemplateColumns: {
          xs: '1fr',
          md: 'minmax(520px, 1.05fr) minmax(440px, 0.95fr)',
        },
        background: 'var(--bg-base)',
        color: 'var(--fg-default)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* ───────── LEFT: 브랜드 패널 (모바일 숨김) ───────── */}
      <Box
        component="aside"
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'relative',
          flexDirection: 'column',
          gap: 3.5,
          padding: '40px 56px 36px',
          overflow: 'hidden',
          borderRight: '0.5px solid var(--border-subtle)',
        }}
      >
        <BrandPanelDecor />

        {/* 상단: 워드마크 */}
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center' }}>
          <Wordmark />
        </Box>

        {/* Hero */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            mt: 2.5,
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(8px)',
            transition: eyeTransition,
          }}
        >
          <Box sx={{ fontSize: 13, fontWeight: 700, color: 'var(--c-green-700)', letterSpacing: '-0.005em' }}>
            사장님 가계부
          </Box>
          <Box
            component="h1"
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: 40,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.18,
              color: 'var(--fg-strong)',
              m: 0,
            }}
          >
            매일 1분 기록,
            <br />
            매월 성장하는 가게.
          </Box>
          <Box
            component="p"
            sx={{
              fontSize: 15,
              lineHeight: 1.6,
              color: 'var(--fg-secondary)',
              fontWeight: 500,
              m: 0,
            }}
          >
            매출·지출·세금까지 한 곳에서. 사장님이 기록하면
            <br />
            AI가 분석해 가게의 흐름을 보여드려요.
          </Box>
        </Box>

        {/* 성장 그래프 카드 */}
        <GrowthChart />

        {/* 가치 제안 bullets */}
        <Box
          component="ul"
          sx={{
            position: 'relative',
            zIndex: 1,
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.75,
            mt: 0.5,
          }}
        >
          <BulletItem n="01" title="하루 1분 기록" desc="매출·지출만 입력하면 끝. AI가 카드·현금을 알아서 분류해요." />
          <BulletItem n="02" title="AI 성장 리포트" desc="전월 대비 추이와 결산을 AI가 자동으로 정리해 드려요." />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            mt: 'auto',
            fontSize: 12,
            color: 'var(--fg-tertiary)',
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <span>© 2026 일기월장</span>
          <Box component="span" sx={{ color: 'var(--fg-disabled)' }}>·</Box>
          <Box component="a" href="#" sx={{ color: 'var(--fg-secondary)', textDecoration: 'none' }}>도움말</Box>
          <Box component="span" sx={{ color: 'var(--fg-disabled)' }}>·</Box>
          <Box component="a" href="#" sx={{ color: 'var(--fg-secondary)', textDecoration: 'none' }}>문의</Box>
        </Box>
      </Box>

      {/* ───────── RIGHT: 인증 패널 ───────── */}
      <Box
        component="main"
        sx={{
          position: 'relative',
          background: 'var(--bg-base)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: '32px 20px', sm: '40px 32px', md: '40px 48px' },
          gap: 2.5,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 380,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.75,
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(10px)',
            transition: cardTransition,
          }}
        >
          {/* 모바일에서 보이는 워드마크 (브랜드 패널 숨겨졌을 때) */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 0.5 }}>
            <Wordmark />
          </Box>

          {/* eyebrow */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: 13, fontWeight: 600, color: 'var(--c-green-700)' }}>
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'var(--c-green-600)',
                boxShadow: '0 0 0 4px var(--c-green-100)',
              }}
            />
            <span>오늘의 기록을 시작합니다</span>
          </Box>

          <Box
            component="h2"
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: 30,
              fontWeight: 800,
              letterSpacing: '-0.025em',
              color: 'var(--fg-strong)',
              m: '-8px 0 0',
              lineHeight: 1.2,
            }}
          >
            프리미엄 로그인
          </Box>

          <Box
            component="p"
            sx={{
              fontSize: 15,
              color: 'var(--fg-secondary)',
              m: '-12px 0 0',
              lineHeight: 1.55,
              fontWeight: 500,
            }}
          >
            PC 버전은{' '}
            <Box component="b" sx={{ color: 'var(--c-green-700)', fontWeight: 700 }}>
              일기월장 프리미엄 사용자
            </Box>
            만 이용하실 수 있습니다.
            <br />
            가입하신 소셜 계정으로 로그인해 주세요.
          </Box>

          {/* 버튼 스택 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 0.5 }}>
            {/* Google */}
            <Box
              component="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              onMouseEnter={() => setHovered('google')}
              onMouseLeave={() => {
                setHovered(null);
                setPressed(null);
              }}
              onMouseDown={() => setPressed('google')}
              onMouseUp={() => setPressed(null)}
              sx={{
                height: 56,
                width: '100%',
                border: '1px solid',
                borderColor:
                  hovered === 'google' ? 'rgba(20,25,30,0.55)' : 'rgba(112,115,124,0.32)',
                borderRadius: 'var(--r-12)',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                alignItems: 'center',
                columnGap: 1.5,
                padding: '0 18px',
                fontSize: 15,
                fontWeight: 600,
                letterSpacing: '-0.005em',
                fontFamily: 'inherit',
                color: 'var(--fg-strong)',
                background:
                  pressed === 'google' ? '#F4F4F5' : hovered === 'google' ? '#FAFAFA' : '#FFFFFF',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                transform: pressed === 'google' ? 'translateY(1px)' : 'translateY(0)',
                transition: 'background var(--motion-fast), border-color var(--motion-fast), transform 80ms ease',
              }}
            >
              {loading ? <CircularProgress size={18} sx={{ color: 'var(--fg-strong)' }} /> : <GoogleIcon />}
              <Box component="span" sx={{ textAlign: 'left' }}>Google로 계속하기</Box>
              <Box component="span" sx={{ fontSize: 18, fontWeight: 500, color: 'rgba(20,25,30,0.45)' }}>→</Box>
            </Box>

            {/* Apple */}
            <Box
              component="button"
              onClick={handleAppleLogin}
              disabled={loading}
              onMouseEnter={() => setHovered('apple')}
              onMouseLeave={() => {
                setHovered(null);
                setPressed(null);
              }}
              onMouseDown={() => setPressed('apple')}
              onMouseUp={() => setPressed(null)}
              sx={{
                height: 56,
                width: '100%',
                border: '1px solid transparent',
                borderRadius: 'var(--r-12)',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                alignItems: 'center',
                columnGap: 1.5,
                padding: '0 18px',
                fontSize: 15,
                fontWeight: 600,
                letterSpacing: '-0.005em',
                fontFamily: 'inherit',
                color: '#FFFFFF',
                background:
                  pressed === 'apple' ? '#000' : hovered === 'apple' ? '#1A1A1A' : '#171719',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transform: pressed === 'apple' ? 'translateY(1px)' : 'translateY(0)',
                transition: 'background var(--motion-fast), transform 80ms ease',
              }}
            >
              {loading ? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} /> : <AppleIcon />}
              <Box component="span" sx={{ textAlign: 'left' }}>Apple로 계속하기</Box>
              <Box component="span" sx={{ fontSize: 18, fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>→</Box>
            </Box>
          </Box>

          {/* 프리미엄 힌트 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: '14px 16px',
              background: 'var(--c-green-50)',
              border: '0.5px solid rgba(0,191,64,0.22)',
              borderRadius: 'var(--r-12)',
              fontSize: 13,
              color: 'var(--c-green-800)',
              fontWeight: 500,
              lineHeight: 1.5,
            }}
          >
            <ShieldIcon />
            <span>
              <Box component="b" sx={{ fontWeight: 700 }}>프리미엄 전용 영역</Box>{' '}
              · 모바일에서 가입한 계정으로 로그인됩니다.
            </span>
          </Box>

          {/* 로그인된 상태인데 프리미엄이 아닌 경우 추가 안내 */}
          {isAuthenticatedNotPremium && (
            <Box
              sx={{
                padding: '14px 16px',
                background: 'var(--primary-bg)',
                borderRadius: 'var(--r-12)',
                border: '0.5px solid var(--primary-soft)',
                fontSize: 13,
                color: 'var(--primary-press)',
                fontWeight: 500,
                lineHeight: 1.55,
                textAlign: 'center',
              }}
            >
              모바일 앱에서 프리미엄 가입 후
              <br />
              웹 대시보드를 이용하실 수 있습니다.
            </Box>
          )}

          {/* 약관 */}
          <Box
            sx={{
              fontSize: 12,
              color: 'var(--fg-tertiary)',
              textAlign: 'left',
              lineHeight: 1.6,
              mt: -0.5,
            }}
          >
            계속하면{' '}
            <Box
              component="a"
              href="#"
              sx={{
                color: 'var(--fg-secondary)',
                textDecoration: 'underline',
                textDecorationColor: 'rgba(112,115,124,0.30)',
                textUnderlineOffset: 2,
              }}
            >
              이용약관
            </Box>{' '}
            ·{' '}
            <Box
              component="a"
              href="#"
              sx={{
                color: 'var(--fg-secondary)',
                textDecoration: 'underline',
                textDecorationColor: 'rgba(112,115,124,0.30)',
                textUnderlineOffset: 2,
              }}
            >
              개인정보처리방침
            </Box>
            에 동의하게 됩니다.
          </Box>
        </Box>

        {/* 카드 아래: 도움 안내 */}
        <Box
          sx={{
            fontSize: 13,
            color: 'var(--fg-tertiary)',
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          이용에 문제가 있으신가요?
          <Box
            component="a"
            href="tel:01094064315"
            sx={{
              color: 'var(--c-green-700)',
              textDecoration: 'none',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.625,
              fontSize: 14,
              letterSpacing: '-0.01em',
            }}
          >
            <PhoneIcon />
            <span>010-9406-4315</span>
          </Box>
        </Box>
      </Box>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/* ───────────────────────────────────────────────
 * 브랜드 패널 장식 (그라데이션 + orb + 점박이)
 * ─────────────────────────────────────────────── */
const BrandPanelDecor: React.FC = () => (
  <>
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        background: 'linear-gradient(155deg, #F2FFF6 0%, #FFFFFF 55%, #F7F7F8 100%)',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        top: -120,
        left: -100,
        width: 380,
        height: 380,
        zIndex: 0,
        background: 'radial-gradient(circle, rgba(0,191,64,0.18) 0%, rgba(0,191,64,0) 70%)',
        pointerEvents: 'none',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: -160,
        right: -120,
        width: 420,
        height: 420,
        zIndex: 0,
        background: 'radial-gradient(circle, rgba(0,191,64,0.10) 0%, rgba(0,191,64,0) 70%)',
        pointerEvents: 'none',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(0,150,50,0.18) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
        maskImage: 'linear-gradient(135deg, #000 0%, #000 30%, transparent 80%)',
        WebkitMaskImage: 'linear-gradient(135deg, #000 0%, #000 30%, transparent 80%)',
        opacity: 0.45,
      }}
    />
  </>
);

const Wordmark: React.FC = () => (
  <Box
    sx={{
      fontFamily: 'var(--font-display)',
      fontSize: 28,
      fontWeight: 800,
      letterSpacing: '-0.03em',
      display: 'inline-flex',
      alignItems: 'baseline',
      gap: '1px',
      lineHeight: 1,
    }}
  >
    <Box component="span" sx={{ color: 'var(--c-green-700)', fontWeight: 900 }}>일</Box>
    <Box component="span" sx={{ color: 'var(--fg-strong)' }}>기월장</Box>
    <Box component="span" sx={{ color: 'rgba(112,115,124,0.40)', fontWeight: 400, mx: 1, fontSize: 18 }}>/</Box>
    <Box
      component="span"
      sx={{
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: '0.06em',
        color: 'var(--fg-tertiary)',
        textTransform: 'uppercase',
        alignSelf: 'center',
      }}
    >
      ilgi-wol-jang
    </Box>
  </Box>
);

const BulletItem: React.FC<{ n: string; title: string; desc: string }> = ({ n, title, desc }) => (
  <Box component="li" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75 }}>
    <Box
      component="span"
      sx={{
        flex: 'none',
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--c-green-700)',
        letterSpacing: '0.05em',
        width: 26,
        pt: 0.375,
      }}
    >
      {n}
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Box sx={{ fontSize: 15, fontWeight: 700, color: 'var(--fg-strong)', letterSpacing: '-0.01em' }}>
        {title}
      </Box>
      <Box sx={{ fontSize: 14, color: 'var(--fg-secondary)', fontWeight: 500, lineHeight: 1.5 }}>
        {desc}
      </Box>
    </Box>
  </Box>
);

/* ───────────────────────────────────────────────
 * 6개월 매출/지출/순이익 추이 — 대시보드 RevenueExpenseChart와 동일 시각.
 * 데모 데이터 (마케팅용, 실 데이터 아님).
 * ─────────────────────────────────────────────── */

// 데모 데이터 — 매월 성장하는 가게 스토리 (1820만 → 3240만, +78%)
const DEMO_CHART_DATA = [
  { month: '11월', revenue: 18200000, expense: 14000000, netIncome: 4200000 },
  { month: '12월', revenue: 21400000, expense: 16200000, netIncome: 5200000 },
  { month: '1월',  revenue: 19600000, expense: 15000000, netIncome: 4600000 },
  { month: '2월',  revenue: 23800000, expense: 17000000, netIncome: 6800000 },
  { month: '3월',  revenue: 28200000, expense: 19000000, netIncome: 9200000 },
  { month: '4월',  revenue: 32400000, expense: 20500000, netIncome: 11900000 },
];

const formatCurrency = (value: number) => value.toLocaleString('ko-KR');

const ChartTooltip: React.FC<{ active?: boolean; payload?: any[]; label?: string }> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <Box
      sx={{
        backgroundColor: 'var(--bg-base)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--r-12)',
        p: 1.5,
        boxShadow: 'var(--shadow-3)',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700, fontSize: 13 }}>
        {label}
      </Typography>
      {payload.map((entry, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: entry.color }} />
          <Typography variant="body2" sx={{ color: 'var(--fg-tertiary)', fontSize: 12 }}>
            {entry.name === 'revenue' ? '매출' : entry.name === 'expense' ? '지출' : '순이익'}:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12, fontFamily: 'var(--font-mono)' }}>
            {formatCurrency(entry.value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const GrowthChart: React.FC = () => {
  const last = DEMO_CHART_DATA[DEMO_CHART_DATA.length - 1];
  const first = DEMO_CHART_DATA[0];
  const growth = Math.round(((last.revenue / first.revenue) - 1) * 100);

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        background: 'var(--bg-base)',
        border: '0.5px solid var(--border-subtle)',
        borderRadius: 'var(--r-20)',
        padding: '20px 20px 12px',
        boxShadow: '0 1px 2px rgba(23,23,23,0.04), 0 8px 24px rgba(23,23,23,0.05)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      {/* 헤더 — 라벨 + 매출 + 성장률 칩 */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Box sx={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-tertiary)', letterSpacing: '-0.005em' }}>
            최근 6개월 수익/비용 추이
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 1 }}>
            <Box
              component="span"
              sx={{
                fontFamily: 'var(--font-display)',
                fontSize: 26,
                fontWeight: 800,
                color: 'var(--fg-strong)',
                letterSpacing: '-0.025em',
              }}
            >
              32,400,000
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--c-green-700)',
                background: 'var(--c-green-100)',
                padding: '2px 8px 2px 6px',
                borderRadius: 999,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                <path d="M5 1.5L8.5 6H6V8.5H4V6H1.5z" fill="#00BF40" />
              </svg>
              +{growth}%
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--fg-strong)',
            background: 'var(--c-cool-100)',
            padding: '4px 10px',
            borderRadius: 999,
          }}
        >
          데모
        </Box>
      </Box>

      {/* Recharts ComposedChart — 대시보드와 동일 시각 (Bar 매출 + Line 지출 + Area 순이익) */}
      <Box sx={{ height: 200, width: '100%', mt: 0.5 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={DEMO_CHART_DATA} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(112,115,124,0.18)" />
            <XAxis
              dataKey="month"
              stroke="rgba(55,56,60,0.61)"
              fontSize={11}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="rgba(55,56,60,0.61)"
              fontSize={11}
              tickFormatter={formatCurrency}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip content={<ChartTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: 8, fontSize: '11px' }}
              iconSize={10}
              formatter={(value) => (
                <span style={{ color: 'var(--fg-secondary)', fontSize: '11px', marginLeft: 4 }}>
                  {value === 'revenue' ? '매출' : value === 'expense' ? '지출' : '순이익'}
                </span>
              )}
            />

            {/* 매출 - 막대 */}
            <Bar
              dataKey="revenue"
              name="revenue"
              fill={getFinancialColor('income')}
              opacity={0.85}
              radius={[3, 3, 0, 0]}
              barSize={18}
            />

            {/* 지출 - 라인 */}
            <Line
              type="monotone"
              dataKey="expense"
              name="expense"
              stroke={getFinancialColor('expense')}
              strokeWidth={2.5}
              dot={{ fill: getFinancialColor('expense'), strokeWidth: 0, r: 3 }}
              activeDot={{ r: 5, stroke: getFinancialColor('expense'), strokeWidth: 2 }}
            />

            {/* 순이익 - 영역 */}
            <Area
              type="monotone"
              dataKey="netIncome"
              name="netIncome"
              fill="#14191E"
              stroke="#14191E"
              strokeWidth={1.5}
              fillOpacity={0.2}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

/* ───────────────────────────────────────────────
 * 아이콘
 * ─────────────────────────────────────────────── */
const GoogleIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.614z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
    />
  </svg>
);

const AppleIcon: React.FC = () => (
  <svg width="18" height="20" viewBox="0 0 16 18" fill="#fff" aria-hidden="true">
    <path d="M13.13 9.55c-.02-2.18 1.78-3.23 1.86-3.28-1.01-1.48-2.59-1.68-3.16-1.71-1.34-.13-2.62.79-3.31.79-.69 0-1.74-.77-2.86-.75C4.2 4.62 2.87 5.43 2.13 6.7.6 9.27 1.74 13.07 3.22 15.16c.73 1.02 1.6 2.17 2.74 2.13 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.71.71 2.87.69 1.19-.02 1.94-1.04 2.66-2.07.84-1.19 1.18-2.34 1.2-2.4-.03-.01-2.3-.88-2.32-3.5z" />
    <path d="M11.06 3.04c.61-.74 1.02-1.77.91-2.79-.88.04-1.95.59-2.58 1.32-.56.65-1.06 1.69-.93 2.7.98.08 1.99-.5 2.6-1.23z" />
  </svg>
);

const PhoneIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ flex: 'none' }}>
    <path
      d="M3 1.5h2l1 2.5-1.4 1A6 6 0 0 0 7 7.4l1-1.4 2.5 1V9.5A1.5 1.5 0 0 1 9 11 8 8 0 0 1 1 3a1.5 1.5 0 0 1 1.5-1.5z"
      stroke="#009632"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const ShieldIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{ flex: 'none' }}>
    <path
      d="M7 1.5L2 3.2v3.5C2 9.6 4.2 11.8 7 12.5c2.8-.7 5-2.9 5-5.8V3.2L7 1.5z"
      stroke="#009632"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path d="M5 7l1.4 1.4L9 5.8" stroke="#009632" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default LoginPage;
